import React, { useState, useEffect, useRef } from "react";
import { useStripeAppearance } from "@/lib/stripeAppearance";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Loader2, Lock, Coins, X, CheckCircle2 } from "lucide-react";
import { getStripe } from "@/lib/stripe";
import { CoinPrice, CopyWithCoinPrice, COIN_PRICE_SLOT } from "@/components/common/CoinPrice";
import { centsForCoins } from "@/lib/coins";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { isRateLimited, RATE_LIMITED_KEY } from "@/lib/apiErrors";
import { gaEcommerce, gaItem, trackPurchase } from "@/lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "samtwo_platform_token";
const authHeaders = () => {
  const token = localStorage.getItem(TOKEN_KEY) || localStorage.getItem("samtwo_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Reuse the tips card-error vocabulary so we never render Stripe's raw message.
const STRIPE_ERR = {
  card_declined: "tips.errCardDeclined",
  insufficient_funds: "tips.errInsufficientFunds",
  expired_card: "tips.errExpiredCard",
  incorrect_cvc: "tips.errIncorrectCvc",
  invalid_cvc: "tips.errIncorrectCvc",
  incorrect_number: "tips.errIncorrectNumber",
  invalid_number: "tips.errIncorrectNumber",
  processing_error: "tips.errProcessingError",
};
const STRIPE_LOCALES = { fr: "fr", es: "es", pt: "pt", ru: "ru", ar: "ar", zh: "zh" };

const CardStep = ({ cents, paymentIntentId, onDone, onError }) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const pay = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true); setError("");
    const { error: err, paymentIntent } = await stripe.confirmPayment({ elements, redirect: "if_required" });
    if (err) {
      setError(t(STRIPE_ERR[err.decline_code] || STRIPE_ERR[err.code] || "buyCoins.error"));
      setBusy(false);
      return;
    }
    if (paymentIntent && paymentIntent.status === "succeeded") {
      trackPurchase({ eventId: paymentIntent.id, category: "coins", purchaseType: "topup",
        items: [gaItem({ id: "coins", name: "Coin top-up", category: "coins", priceCents: paymentIntent.amount })],
        valueCents: paymentIntent.amount, currency: (paymentIntent.currency || "usd").toUpperCase() });
      try {
        const { data } = await axios.post(`${API}/coins/topup/confirm`, { payment_intent_id: paymentIntent.id }, { headers: authHeaders() });
        onDone(data.wallet);
      } catch {
        onError(t("buyCoins.error"));
      }
      return;
    }
    setError(t("buyCoins.error"));
    setBusy(false);
  };

  return (
    <form onSubmit={pay} data-testid="buycoins-card-form" className="mt-4">
      <PaymentElement options={{ layout: "tabs" }} />
      {error && <p className="text-sm mt-3" style={{ color: "#ef4444" }} data-testid="buycoins-card-error">{error}</p>}
      <button type="submit" disabled={!stripe || busy} data-testid="buycoins-pay-btn"
        className="w-full mt-4 py-3 rounded-full text-sm font-semibold flex items-center justify-center gap-2 metallic-sheen"
        style={{ color: "#fff", opacity: busy ? 0.7 : 1 }}>
        {busy ? <><Loader2 size={16} className="animate-spin" /> {t("buyCoins.processing")}</>
          : <><Lock size={14} /> <CopyWithCoinPrice template={t("buyCoins.payBtn", { price: COIN_PRICE_SLOT })} cents={cents} size="sm" /></>}
      </button>
    </form>
  );
};

export const BuyCoinsPanel = ({ open, onOpenChange, onCredited }) => {
  const stripeAppearance = useStripeAppearance();
  const { t, i18n } = useTranslation();
  const [packs, setPacks] = useState([]);
  const [bounds, setBounds] = useState({ min: 25, max: 25000 });
  const [custom, setCustom] = useState("");
  const [stage, setStage] = useState("select"); // select | pay | result
  const [clientSecret, setClientSecret] = useState("");
  const [customerSessionSecret, setCustomerSessionSecret] = useState("");
  const [piId, setPiId] = useState("");
  const [payCents, setPayCents] = useState(0);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");
  const [resultWallet, setResultWallet] = useState(null);
  const fetched = useRef(false);

  useEffect(() => {
    if (!open || fetched.current) return;
    fetched.current = true;
    axios.get(`${API}/coins/packs`).then(({ data }) => {
      setPacks(data.packs || []);
      setBounds({ min: data.min_coins, max: data.max_coins });
    }).catch(() => {});
  }, [open]);

  const customCoins = custom === "" ? null : parseInt(custom, 10);
  const customValid = Number.isFinite(customCoins) && customCoins >= bounds.min && customCoins <= bounds.max;
  const [owedCoins, setOwedCoins] = useState(0);
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/coins/wallet`, { headers: authHeaders() })
      .then(({ data }) => setOwedCoins(data.balance_cents < 0 ? Math.abs(data.balance_coins) : 0))
      .catch(() => {});
  }, [open]);

  const reset = () => {
    setStage("select"); setClientSecret(""); setCustomerSessionSecret(""); setPiId("");
    setPayCents(0); setStarting(false); setError(""); setResultWallet(null); setCustom("");
  };

  const close = () => { onOpenChange(false); setTimeout(reset, 200); };

  const start = async ({ packId = null, coins = null }) => {
    if (starting) return;
    setError(""); setStarting(true);
    try {
      const { data } = await axios.post(`${API}/coins/topup/create`,
        { pack_id: packId, coins }, { headers: authHeaders() });
      setClientSecret(data.client_secret);
      setCustomerSessionSecret(data.customer_session_client_secret || "");
      setPiId(data.payment_intent_id || "");
      setPayCents(data.cents);
      setStage("pay");
      gaEcommerce("begin_checkout", { items: [gaItem({ id: "coins", name: "Coin top-up", category: "coins", priceCents: data.cents })], valueCents: data.cents, checkout_type: "coins" });
    } catch (e) {
      setError(t(isRateLimited(e) ? RATE_LIMITED_KEY : e?.response?.data?.detail?.reason === "invalid_amount" ? "buyCoins.rangeError" : "buyCoins.error",
        { min: bounds.min, max: bounds.max }));
    } finally { setStarting(false); }
  };

  const onDone = (wallet) => { setResultWallet(wallet); setStage("result"); if (onCredited) onCredited(wallet); };

  const body = stage === "result" ? (
    <div className="text-center py-6" data-testid="buycoins-result">
      <CheckCircle2 size={34} className="mx-auto mb-3" style={{ color: "var(--accent-primary)" }} />
      <p className="text-base font-bold mb-1" style={{ color: "var(--text-primary)" }} data-testid="buycoins-success">{t("buyCoins.successTitle")}</p>
      <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
        <CopyWithCoinPrice template={t("buyCoins.successBody", { price: COIN_PRICE_SLOT })} coins={resultWallet ? resultWallet.balance_coins : 0} size="sm" />
      </p>
      <button onClick={close} className="mt-5 text-sm font-semibold" style={{ color: "var(--accent-primary)" }} data-testid="buycoins-done-btn">{t("buyCoins.done")}</button>
    </div>
  ) : stage === "pay" ? (
    <div className="mt-2">
      <p className="text-sm font-semibold mb-1" style={{ color: "var(--text-primary)" }}>
        <CopyWithCoinPrice template={t("buyCoins.payingFor", { price: COIN_PRICE_SLOT })} cents={payCents} size="sm" />
      </p>
      <Elements stripe={getStripe()} options={{ clientSecret, customerSessionClientSecret: customerSessionSecret || undefined, locale: STRIPE_LOCALES[i18n.language] || undefined, appearance: stripeAppearance }}>
        <CardStep cents={payCents} paymentIntentId={piId} onDone={onDone} onError={(m) => { setError(m); setStage("select"); }} />
      </Elements>
    </div>
  ) : (
    <div className="mt-2">
      {owedCoins > 0 && (
        <p className="text-sm mb-3 rounded-xl px-3 py-2.5" style={{ background: "var(--bg-subtle)", border: "1px solid #ef4444", color: "var(--text-secondary)" }} data-testid="buycoins-negative">{t("buyCoins.negativeNotice")}</p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="buycoins-packs">
        {packs.map((p) => (
          <div key={p.id} className="rounded-2xl p-4 flex flex-col items-center text-center gap-3"
            style={{ background: "var(--bg-subtle)", border: "1px solid var(--border-color)" }} data-testid={`buycoins-pack-${p.id}`}>
            <CoinPrice cents={p.cents} coins={p.coins} size="md" align="center" stacked />
            <button onClick={() => start({ packId: p.id })} disabled={starting} data-testid={`buycoins-buy-${p.id}`}
              className="w-full py-2.5 rounded-full text-sm font-semibold transition-transform hover:-translate-y-0.5 metallic-sheen"
              style={{ color: "#fff", opacity: starting ? 0.6 : 1 }}>
              {t("buyCoins.buyNow")}
            </button>
          </div>
        ))}
      </div>

      <div className="mt-5 pt-5" style={{ borderTop: "1px solid var(--border-subtle)" }}>
        <p className="text-sm font-semibold mb-2" style={{ color: "var(--text-primary)" }}>{t("buyCoins.customTitle")}</p>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <div className="relative flex-1">
            <Coins size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-secondary)" }} />
            <input type="number" min={bounds.min} max={bounds.max} step="1" value={custom}
              onChange={(e) => setCustom(e.target.value)} placeholder={t("buyCoins.customPlaceholder")}
              data-testid="buycoins-custom-input"
              className="w-full pl-9 pr-3 py-2.5 rounded-xl text-sm outline-none"
              style={{ background: "var(--bg-subtle)", border: `1px solid ${custom && !customValid ? "#ef4444" : "var(--border-color)"}`, color: "var(--text-primary)" }} />
          </div>
          <button onClick={() => start({ coins: customCoins })} disabled={!customValid || starting} data-testid="buycoins-custom-buy"
            className="py-2.5 px-5 rounded-full text-sm font-semibold whitespace-nowrap metallic-sheen"
            style={{ color: "#fff", opacity: (!customValid || starting) ? 0.5 : 1, cursor: customValid ? "pointer" : "not-allowed" }}>
            {customValid
              ? <CopyWithCoinPrice template={t("buyCoins.buyAmount", { price: COIN_PRICE_SLOT })} cents={centsForCoins(customCoins)} size="sm" />
              : t("buyCoins.buyNow")}
          </button>
        </div>
        <p className="text-sm mt-2" style={{ color: custom && !customValid ? "#ef4444" : "var(--text-secondary)" }} data-testid="buycoins-custom-hint">
          {t("buyCoins.customHint", { min: bounds.min, max: bounds.max })}
        </p>
      </div>

      {error && <p className="text-sm mt-3" style={{ color: "#ef4444" }} data-testid="buycoins-error">{error}</p>}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent data-testid="buycoins-panel" className="max-w-2xl"
        style={{ maxHeight: "92vh", display: "flex", flexDirection: "column", padding: 0, gap: 0, overflow: "hidden" }}>
        <div className="px-6 pt-6 pb-3 shrink-0" style={{ borderBottom: "1px solid var(--border-subtle)" }}>
          <DialogTitle className="text-xl font-bold pr-8" style={{ color: "var(--text-primary)" }}>{t("buyCoins.title")}</DialogTitle>
          <DialogDescription className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>{t("buyCoins.subtitle")}</DialogDescription>
        </div>
        <div className="overflow-y-auto px-6 pb-6" style={{ flex: 1 }} data-testid="buycoins-scroll">
          {body}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BuyCoinsPanel;
